<template>
  <div class="upload-payouts-registry">
    <div class="page__header">
      <div class="page__title">
        <router-link :to="backLinkRoute" class="page__back-link">
          <arrow-button
            class="page__back-link-icon"
            :rotated180="true"
          ></arrow-button>
        </router-link>
        <div class="page__title-text">
          {{ $t('payouts.by-registry.upload-page.bill-registries-title') }}
        </div>
      </div>
    </div>
    <div class="upload-payouts-registry__form">
      <div class="upload-payouts-registry__form-fields">
        <div class="upload-payouts-registry__form-field">
          <text-input
            :title="$t('payouts.by-registry.upload-page.fields.registry-name')"
            v-model:value="registryFileData.name"
          ></text-input>
        </div>
        <div
          class="upload-payouts-registry__form-field"
          v-show="!isBillsRegistryUploading"
        >
          <file-input-big
            accept=".xlsx,.csv"
            v-model:value="registryFileData.file"
            :placeholder="$t('bills.popup.by-registry-format.file-placeholder')"
          >
          </file-input-big>
          <div
            class="upload-payouts-registry__form-field-information"
            @click="openInformationPopup"
          >
            {{ $t('bills.by-registry.information-hint') }}
          </div>
        </div>
        <div
          class="upload-payouts-registry__form-loader"
          v-if="isBillsRegistryUploading"
        >
          <div class="upload-payouts-registry__form-loader-title">
            {{
              $t('bills.by-registry.upload-page.loader.title', {
                name: registryFileData.file.name,
              })
            }}
          </div>
          <loader></loader>
        </div>
      </div>
      <primary-button
        class="upload-payouts-registry__form-action"
        :disabled="v$.$silentErrors.length > 0"
        :label="$t('bills.by-registry.upload-page.actions.create')"
        @click="uploadBillsRegistry"
      ></primary-button>
    </div>
    <bill-registry-format-popup
      ref="informationPopup"
    ></bill-registry-format-popup>
    <success-notification
      :text="
        $t('bills.by-registry.upload-page.notifications.success', {
          name: registryFileData.name,
        })
      "
      ref="successNotification"
    ></success-notification>
  </div>
</template>

<script>
import ArrowButton from '@vue-app/components/base/button/ArrowButton.vue'
import TextInput from '@vue-app/components/base/input/TextInput.vue'
import FileInput from '@vue-app/components/base/input/FileInput.vue'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { fileValidator } from '@js-app/services/validators'
import PrimaryButton from '@vue-app/components/base/button/PrimaryButton.vue'
import FileInputBig from '@vue-app/components/base/input/FileInputBig.vue'
import PayoutsRegistryFormatPopup from '@vue-app/components/company/payouts/by-registry/popup/PayoutsRegistryFormatPopup.vue'
import BillRegistryFormatPopup from '@vue-app/components/company/bills/popup/BillRegistryFormatPopup.vue'
import { CompaniesApi } from '@js-app/office/api/companies-api'
import { mapState } from 'vuex'
import Loader from '@vue-app/components/base/Loader.vue'
import SuccessNotification from '@vue-app/components/base/popup/notification/SuccessNotification.vue'

export default {
  name: 'UploadBillRegistry',
  components: {
    SuccessNotification,
    Loader,
    BillRegistryFormatPopup,
    FileInputBig,
    PrimaryButton,
    FileInput,
    TextInput,
    ArrowButton,
  },

  setup: () => ({
    v$: useVuelidate(),
  }),
  props: {
    id: [String, Number],
  },

  data() {
    return {
      registryFileData: {
        name: '',
        file: null,
      },
      isBillsRegistryUploading: false,
    }
  },
  validations() {
    return {
      registryFileData: {
        name: {
          required,
        },
        file: {
          required,
          fileValidator: fileValidator([
            'text/csv',
            'text/xlsx',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          ]),
        },
      },
    }
  },
  computed: {
    ...mapState(['selectedCompany']),
    backLinkRoute() {
      return `/cabinet/company/${this.id}/bills#registry`
    },
  },
  methods: {
    openInformationPopup() {
      this.$refs.informationPopup.open()
    },

    async uploadBillsRegistry() {
      if (this.isBillsRegistryUploading) {
        return
      }

      this.isBillsRegistryUploading = true
      this.registryFileData['type'] = this.registryFileData.file.type

      const formData = new FormData()
      formData.append('file', this.registryFileData.file)
      formData.append('registry_name', this.registryFileData.name)

      try {
        this.isBillsRegistryUploading = true
        let _billsRegistryResponse = await CompaniesApi.loadBillsRegistry(
          this.selectedCompany,
          formData
        )

        await this.$refs.successNotification.show()
        this.$router.push({
          name: 'bills-registry',
          params: {
            id: this.selectedCompany,
            billsRegistryId: _billsRegistryResponse.data.id,
          },
        })
      } catch (e) {
        console.error(e)
      } finally {
        this.isBillsRegistryUploading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.upload-payouts-registry {
  margin-top: 30px;
  &__form {
    margin-top: 24px;
    width: 390px;
    background: #ffffff;
    border-radius: 22px;
    padding: 50px 50px 65px;

    &-field {
      margin-bottom: 18px;

      &-information {
        display: inline-block;
        margin-top: 10px;
        font-size: 12px;
        line-height: 14px;
        text-decoration: underline;
        color: #64748B;
        cursor: pointer;

        &:hover {
          text-decoration-color: transparent;
        }
      }
    }

    &-action {
      margin-top: 40px;
      padding: 25px;
    }

    &-loader {
      &-title {
        font-weight: 500;
        font-size: 14px;
        line-height: 20px;
        color: #64748B;
        text-align: center;
      }
    }
  }
}
</style>
